import type { Projectile } from "../entities/Projectile.ts";
import type { Vec2 } from "../core/types.ts";
import { glowCache } from "./GlowSpriteCache.ts";
import { deriveRamp, emissiveFill } from "./EmissiveMaterial.ts";
import type { BossPathColor } from "./BossPathColor.ts";
import { COLORS } from "../core/constants.ts";
import {
  initPathInkFromLocation,
  pathGlowInner,
  pathGlowOuter,
} from "./PathInk.ts";

// Seed from `?path=` once, at module load, for the web build. The native shell
// has no address bar — there the review chip drives it.
initPathInkFromLocation();

/**
 * BL-287 — the trench: a dark bed laid UNDER the conduit, slightly wider than
 * its glow. This is the "Materialität hochwertiger, aber keine neue Funktion"
 * half of the owner's brief. The path used to be a flat 8 px stroke sitting ON
 * the board — a highlighter over the grid. Cutting a dark channel first makes
 * the same green read as light *inside* a recess: the grid lines it crosses are
 * occluded rather than overlaid, which is the cheapest honest depth cue there
 * is, and it costs one extra stroke on a path that is already stroked twice.
 * Carries no state and no colour of its own, so it is unaffected by the boss
 * recolour and needs no reduced-motion variant.
 */
const PATH_BED = "rgba(0,0,0,0.38)";
const PATH_BED_WIDTH = 13;

/**
 * Renders projectiles (trail + head) and the enemy path visualization.
 */
export class ProjectileRenderer {
  private ctx: CanvasRenderingContext2D;

  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
  }

  render(projectiles: Projectile[]): void {
    const ctx = this.ctx;

    for (const proj of projectiles) {
      if (!proj.active) continue;

      const dx = (proj.target?.active ? proj.target.x : proj.targetX) - proj.x;
      const dy = (proj.target?.active ? proj.target.y : proj.targetY) - proj.y;
      const angle = Math.atan2(dy, dx);
      const len = proj.radius * 3;

      let arcOffset = 0;
      if (proj.lobbed) {
        const tx = proj.targetX - proj.startX;
        const ty = proj.targetY - proj.startY;
        const total = Math.sqrt(tx * tx + ty * ty);
        const rx = proj.targetX - proj.x;
        const ry = proj.targetY - proj.y;
        const remaining = Math.sqrt(rx * rx + ry * ry);
        const progress =
          total > 0 ? Math.max(0, Math.min(1, 1 - remaining / total)) : 1;
        arcOffset = Math.sin(progress * Math.PI) * proj.arcHeight;

        ctx.fillStyle = "#000000";
        ctx.globalAlpha = 0.2;
        ctx.beginPath();
        if (typeof ctx.ellipse === "function") {
          ctx.ellipse(proj.x, proj.y, 4.5, 2.4, 0, 0, Math.PI * 2);
        } else {
          ctx.arc(proj.x, proj.y, 3, 0, Math.PI * 2);
        }
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      // Trail (ring-buffer positions) — CR-8
      if (proj.trail.length > 1) {
        for (let i = 0; i < proj.trail.length - 1; i++) {
          const t = proj.trail[i]!;
          const alpha = ((i + 1) / proj.trail.length) * 0.5;
          ctx.fillStyle = proj.color;
          ctx.globalAlpha = alpha;
          ctx.beginPath();
          ctx.arc(t.x, t.y - arcOffset, 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }

      // Glow sprite instead of shadowBlur
      glowCache.draw(ctx, proj.color, 8, proj.x, proj.y - arcOffset, 0.6);

      const hy = proj.y - arcOffset;

      if (proj.lobbed) {
        // Lobbed AoE round (virus): a round, organic blob — NOT a directional
        // dart — so it reads as a tumbling mortar shell rather than an arrow.
        const blobR = Math.max(4, proj.radius);
        // Outer membrane = lit volume (BL-26/27): emissive ramp from proj colour.
        ctx.fillStyle = emissiveFill(ctx, proj.x, hy, blobR, deriveRamp(proj.color));
        ctx.globalAlpha = 0.9;
        ctx.beginPath();
        ctx.arc(proj.x, hy, blobR, 0, Math.PI * 2);
        ctx.fill();
        // Darker viral core (offset slightly for an organic look)
        ctx.fillStyle = "#1A0A2E";
        ctx.globalAlpha = 0.55;
        ctx.beginPath();
        ctx.arc(
          proj.x + blobR * 0.18,
          hy - blobR * 0.12,
          blobR * 0.45,
          0,
          Math.PI * 2,
        );
        ctx.fill();
        // Bright highlight
        ctx.fillStyle = "#FFFFFF";
        ctx.globalAlpha = 0.85;
        ctx.beginPath();
        ctx.arc(
          proj.x - blobR * 0.3,
          hy - blobR * 0.3,
          blobR * 0.22,
          0,
          Math.PI * 2,
        );
        ctx.fill();
        ctx.globalAlpha = 1;
      } else {
        // Directional dart (fire/water/etc.)
        ctx.strokeStyle = proj.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(proj.x - Math.cos(angle) * len, hy - Math.sin(angle) * len);
        ctx.lineTo(proj.x, hy);
        ctx.stroke();

        // Dart head = lit volume (BL-26/27): emissive core feeds the bloom.
        ctx.fillStyle = emissiveFill(ctx, proj.x, hy, 2, deriveRamp(proj.color));
        ctx.beginPath();
        ctx.arc(proj.x, hy, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  /** Renders the path line (outer glow + inner dashed) and — for the
   *  primary path only — animated data-flow packets. CR-P3 (Bug D)
   *  suppresses packet rendering on breach secondaries so multi-path
   *  scenes don't accumulate 15+ packets visually overlapping at uneven
   *  cadence (which felt "super fast" to the player). Breach paths still
   *  get their dimmed glow+dash line for spatial awareness. */
  renderPath(
    pathPixels: Vec2[],
    time: number,
    opacity = 1.0,
    drawPackets = true,
    bossColor?: BossPathColor,
  ): void {
    if (pathPixels.length < 2) return;
    const ctx = this.ctx;
    const t = time;
    const first = pathPixels[0]!;
    const prevAlpha = ctx.globalAlpha;
    // CR-H5 (LD-22.1-4): outer-scope opacity multiplier — lets renderPipeline
    // dim breach secondary paths (typically 0.55) below the primary (1.0).
    ctx.globalAlpha = prevAlpha * opacity;

    // Path color: dynamic when boss present, otherwise default green
    // Read per call, not per module load: the review chip can switch the ink
    // mid-run, which is the whole point of it existing (the ramps themselves are
    // rebuilt only on change, so this is two string reads).
    const glowOuter = bossColor ? bossColor.glowColor(0.12) : pathGlowOuter();
    const glowInner = bossColor ? bossColor.glowColor(0.35) : pathGlowInner();
    const packetColor = bossColor ? bossColor.packetHex : COLORS.SENTINEL_GREEN;

    // The trench — laid first so everything below reads as light inside it.
    // Round caps/joins are scoped to THIS stroke and restored immediately: the
    // function has two exit paths, so leaving them set would leak the corner
    // style into every renderer that draws after the path in the same frame,
    // and would silently change the glow strokes below (which shipped with the
    // canvas defaults).
    const prevCap = ctx.lineCap;
    const prevJoin = ctx.lineJoin;
    ctx.strokeStyle = PATH_BED;
    ctx.lineWidth = PATH_BED_WIDTH;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < pathPixels.length; i++) {
      ctx.lineTo(pathPixels[i]!.x, pathPixels[i]!.y);
    }
    ctx.stroke();
    ctx.lineCap = prevCap;
    ctx.lineJoin = prevJoin;

    // Outer glow trail
    ctx.strokeStyle = glowOuter;
    ctx.lineWidth = 8;
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < pathPixels.length; i++) {
      ctx.lineTo(pathPixels[i]!.x, pathPixels[i]!.y);
    }
    ctx.stroke();

    // Inner dashed line
    ctx.strokeStyle = glowInner;
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 8]);
    ctx.lineDashOffset = -t * 10;
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < pathPixels.length; i++) {
      ctx.lineTo(pathPixels[i]!.x, pathPixels[i]!.y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.lineDashOffset = 0;

    // Data packets — only on the primary path (CR-P3 Bug D mitigation).
    // Breach paths skip this block to avoid visual packet swarm overlay.
    if (!drawPackets) {
      ctx.globalAlpha = prevAlpha;
      return;
    }
    const totalLen = pathPixels.length - 1;
    for (let p = 0; p < 3; p++) {
      const frac = (t * 0.12 + p * 0.33) % 1;
      const idx = Math.floor(frac * totalLen);
      const sub = frac * totalLen - idx;
      if (idx >= totalLen) continue;
      const a = pathPixels[idx]!;
      const b = pathPixels[idx + 1]!;
      const px = a.x + (b.x - a.x) * sub;
      const py = a.y + (b.y - a.y) * sub;
      glowCache.draw(ctx, packetColor, 3, px, py, 0.8);
      ctx.fillStyle = packetColor;
      ctx.beginPath();
      ctx.arc(px, py, 3, 0, Math.PI * 2);
      ctx.fill();
    }
    // CR-H5: restore caller's alpha
    ctx.globalAlpha = prevAlpha;
  }
}
